import React, { useEffect, useMemo, useState } from "react";
import { api, formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ChevronRight, Upload, Download } from "lucide-react";

const LEVELS = [
  { key: "state", label: "Regions", parentLevel: null },
  { key: "lga", label: "Municipals / Districts", parentLevel: "state" },
  { key: "ward", label: "Constituencies", parentLevel: "lga" },
  { key: "polling_unit", label: "Polling Units", parentLevel: "ward" },
];

export default function AdminHierarchy() {
  const [selection, setSelection] = useState({ state: null, lga: null, ward: null });
  const [data, setData] = useState({ state: [], lga: [], ward: [], polling_unit: [] });
  const [inputs, setInputs] = useState({ state: "", lga: "", ward: "", polling_unit: "" });
  const [codes, setCodes] = useState({ state: "", lga: "", ward: "", polling_unit: "" });
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("state,lga,ward,polling_unit,code\n");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [preflight, setPreflight] = useState(null);
  const [preflightBusy, setPreflightBusy] = useState(false);

  const runPreflight = async () => {
    setPreflightBusy(true);
    try { const { data } = await api.get("/hierarchy/preflight"); setPreflight(data); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail) || "Preflight failed"); }
    finally { setPreflightBusy(false); }
  };

  const load = async (level, parent_id) => {
    const params = { level, parent_id: parent_id === null || parent_id === undefined ? "root" : parent_id };
    const { data: rows } = await api.get("/locations", { params });
    setData(d => ({ ...d, [level]: rows }));
  };

  useEffect(() => { load("state", null); }, []);
  useEffect(() => { if (selection.state) load("lga", selection.state); else setData(d => ({...d, lga: [], ward: [], polling_unit: []})); }, [selection.state]);
  useEffect(() => { if (selection.lga) load("ward", selection.lga); else setData(d => ({...d, ward: [], polling_unit: []})); }, [selection.lga]);
  useEffect(() => { if (selection.ward) load("polling_unit", selection.ward); else setData(d => ({...d, polling_unit: []})); }, [selection.ward]);

  const add = async (level) => {
    const parentMap = { state: null, lga: selection.state, ward: selection.lga, polling_unit: selection.ward };
    const parent_id = parentMap[level];
    if (level !== "state" && !parent_id) return toast.error("Select a parent first");
    const name = inputs[level].trim();
    if (!name) return;
    try {
      await api.post("/locations", { name, level, parent_id, code: codes[level] });
      setInputs(x => ({ ...x, [level]: "" })); setCodes(x => ({ ...x, [level]: "" }));
      load(level, parent_id);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const remove = async (id, level) => {
    if (!confirm("Delete this location and all children? Existing results will also be removed.")) return;
    await api.delete(`/locations/${id}`);
    const parentMap = { state: null, lga: selection.state, ward: selection.lga, polling_unit: selection.ward };
    load(level, parentMap[level]);
    if (level === "state" && selection.state === id) setSelection({ state: null, lga: null, ward: null });
    if (level === "lga" && selection.lga === id) setSelection(s => ({ ...s, lga: null, ward: null }));
    if (level === "ward" && selection.ward === id) setSelection(s => ({ ...s, ward: null }));
  };

  const onFileChosen = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setImportText(text);
  };

  const runImport = async () => {
    setImporting(true); setImportResult(null);
    try {
      const { data } = await api.post("/locations/bulk-import", { csv_text: importText });
      setImportResult(data);
      const total = (data.inserted || 0) + (data.updated || 0);
      toast.success(`Imported ${total} rows (${data.inserted || 0} new, ${data.updated || 0} updated)`);
      load("state", null);
      if (selection.state) load("lga", selection.state);
      if (selection.lga) load("ward", selection.lga);
      if (selection.ward) load("polling_unit", selection.ward);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setImporting(false); }
  };

  const columns = [
    { key: "state", parent: null, active: () => true, sel: selection.state, setSel: (id) => setSelection({ state: id, lga: null, ward: null }) },
    { key: "lga", parent: selection.state, active: () => !!selection.state, sel: selection.lga, setSel: (id) => setSelection(s => ({ ...s, lga: id, ward: null })) },
    { key: "ward", parent: selection.lga, active: () => !!selection.lga, sel: selection.ward, setSel: (id) => setSelection(s => ({ ...s, ward: id })) },
    { key: "polling_unit", parent: selection.ward, active: () => !!selection.ward, sel: null, setSel: () => {} },
  ];

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-[1600px]">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Geography</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2" data-testid="hierarchy-title">Hierarchy</h1>
          <p className="text-zinc-400 text-sm mt-2">Region → Municipal/District → Constituency → Polling Unit</p>
        </div>
        <div className="flex gap-2">
          <button onClick={runPreflight} disabled={preflightBusy} data-testid="preflight-btn"
            className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10 flex items-center gap-2">
            {preflightBusy ? "Scanning..." : "Preflight"}
          </button>
          <button onClick={()=>{
            const token = localStorage.getItem("ec_token");
            window.open(`${process.env.REACT_APP_BACKEND_URL}/api/locations/export?auth=${token}`, "_blank");
          }} data-testid="export-hierarchy-btn"
            className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10 flex items-center gap-2">
            <Download size={14}/> Export CSV
          </button>
          <button onClick={()=>setImportOpen(v=>!v)} data-testid="bulk-import-toggle"
            className="px-4 py-2 border border-white/20 text-xs uppercase tracking-[0.2em] hover:bg-white/10 flex items-center gap-2">
            <Upload size={14}/> Bulk Import CSV
          </button>
        </div>
      </div>

      {preflight && (
        <div className="mt-6 border border-white/10 p-5" data-testid="preflight-card">
          <div className="flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Register Preflight</div>
            <button onClick={()=>setPreflight(null)} className="text-zinc-500 hover:text-white text-xs">×</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
            <div className="border border-white/10 p-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Polling units</div><div className="font-display text-2xl tabular mt-1">{preflight.total_pus.toLocaleString()}</div></div>
            <div className="border border-white/10 p-3"><div className="text-[10px] uppercase tracking-wider text-zinc-500">Constituencies</div><div className="font-display text-2xl tabular mt-1">{preflight.total_wards.toLocaleString()}</div></div>
            <div className={`border p-3 ${preflight.missing_code_count>0?"border-red-500/40":"border-white/10"}`}><div className="text-[10px] uppercase tracking-wider text-zinc-500">Missing code</div><div className={`font-display text-2xl tabular mt-1 ${preflight.missing_code_count>0?"text-red-400":"text-emerald-400"}`}>{preflight.missing_code_count}</div></div>
            <div className={`border p-3 ${preflight.duplicate_codes_count>0?"border-amber-500/40":"border-white/10"}`}><div className="text-[10px] uppercase tracking-wider text-zinc-500">Duplicate codes</div><div className={`font-display text-2xl tabular mt-1 ${preflight.duplicate_codes_count>0?"text-amber-400":"text-emerald-400"}`}>{preflight.duplicate_codes_count}</div></div>
            <div className={`border p-3 ${preflight.empty_wards_count>0?"border-amber-500/40":"border-white/10"}`}><div className="text-[10px] uppercase tracking-wider text-zinc-500">Empty wards</div><div className={`font-display text-2xl tabular mt-1 ${preflight.empty_wards_count>0?"text-amber-400":"text-emerald-400"}`}>{preflight.empty_wards_count}</div></div>
          </div>
          {preflight.missing_code_count===0 && preflight.duplicate_codes_count===0 && preflight.empty_wards_count===0 && (
            <div className="mt-4 text-sm text-emerald-300 flex items-center gap-2">✓ Register is clean — ready for election day.</div>
          )}
          {preflight.duplicate_codes_count > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-amber-400 mb-2">Sample duplicate codes ({Math.min(5, preflight.duplicate_codes_count)}):</div>
              <div className="text-xs text-zinc-300 space-y-1">
                {preflight.duplicate_codes.slice(0,5).map(d => (
                  <div key={d.code}><span className="text-emerald-400 tabular">{d.code}</span> — {d.pus.length} PUs: {d.pus.slice(0,3).map(p=>p.name).join(", ")}</div>
                ))}
              </div>
            </div>
          )}
          {preflight.empty_wards_count > 0 && (
            <div className="mt-4">
              <div className="text-xs uppercase tracking-wider text-amber-400 mb-2">Sample empty constituencies:</div>
              <div className="text-xs text-zinc-400">{preflight.empty_wards.slice(0,10).map(w=>w.name).join(", ")}</div>
            </div>
          )}
        </div>
      )}

      {importOpen && (
        <div className="mt-6 border border-white/10 bg-[#111114] p-5" data-testid="bulk-import-panel">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">Bulk Import</div>
          <p className="text-sm text-zinc-400 mt-2">
            Paste or upload a CSV with header row: <code className="text-emerald-400">state,lga,ward,polling_unit,code</code>.
            Missing parents are auto-created. Existing names are reused (idempotent).
          </p>
          <div className="mt-3 flex items-center gap-2 text-xs">
            <label className="px-3 py-1.5 border border-white/20 hover:bg-white/10 cursor-pointer inline-flex items-center gap-1.5" data-testid="import-file-label">
              <Upload size={12}/> Choose CSV file
              <input type="file" accept=".csv,text/csv" onChange={onFileChosen} className="hidden" data-testid="import-file-input"/>
            </label>
            <button onClick={()=>setImportText("state,lga,ward,polling_unit,code\nLagos,Ikeja,Ward 1,PU-001,001\nLagos,Ikeja,Ward 1,PU-002,002\nLagos,Ikeja,Ward 2,PU-003,003\nLagos,Eti-Osa,Ward 1,PU-004,004\n")}
              className="text-zinc-400 hover:text-white underline underline-offset-4">Load example</button>
          </div>
          <textarea value={importText} onChange={(e)=>setImportText(e.target.value)} rows={10}
            data-testid="import-textarea"
            className="w-full mt-3 bg-black/30 border border-white/10 p-3 font-mono text-xs outline-none focus:border-white/30 resize-y"/>
          <div className="mt-3 flex items-center gap-3">
            <button onClick={runImport} disabled={importing} data-testid="run-import-btn"
              className="px-4 py-2 bg-white text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-zinc-200 disabled:opacity-50">
              {importing ? "Importing..." : "Run Import"}
            </button>
            {importResult && (
              <div className="text-xs text-zinc-400" data-testid="import-result">
                Created: states <span className="text-emerald-400 tabular">{importResult.created.state}</span>,
                LGAs <span className="text-emerald-400 tabular">{importResult.created.lga}</span>,
                wards <span className="text-emerald-400 tabular">{importResult.created.ward}</span>,
                PUs <span className="text-emerald-400 tabular">{importResult.created.polling_unit}</span>
                {importResult.errors?.length ? <span className="text-red-400 ml-2">· {importResult.errors.length} errors</span> : ""}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-8">
        {LEVELS.map((lv, i) => {
          const col = columns[i]; const rows = data[lv.key];
          return (
            <div key={lv.key} className={`border border-white/10 bg-[#111114] flex flex-col ${!col.active() ? "opacity-40" : ""}`}>
              <div className="px-4 py-3 border-b border-white/10 flex items-center justify-between">
                <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">{lv.label}</div>
                <div className="text-[10px] text-zinc-500 tabular">{rows.length}</div>
              </div>
              <div className="p-3 border-b border-white/10 space-y-2">
                <input placeholder="Name" value={inputs[lv.key]} onChange={(e)=>setInputs({...inputs, [lv.key]: e.target.value})}
                  disabled={!col.active()} data-testid={`add-${lv.key}-name`}
                  onKeyDown={(e)=>e.key==="Enter" && add(lv.key)}
                  className="w-full bg-transparent border-b border-white/20 py-1.5 text-sm outline-none focus:border-white disabled:opacity-50"/>
                <div className="flex gap-2">
                  <input placeholder="Code" value={codes[lv.key]} onChange={(e)=>setCodes({...codes, [lv.key]: e.target.value})}
                    disabled={!col.active()} data-testid={`add-${lv.key}-code`}
                    className="flex-1 bg-transparent border-b border-white/20 py-1.5 text-xs outline-none focus:border-white disabled:opacity-50"/>
                  <button onClick={()=>add(lv.key)} disabled={!col.active()} data-testid={`add-${lv.key}-btn`}
                    className="p-1.5 border border-white/20 hover:bg-white/10 disabled:opacity-30"><Plus size={14}/></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto max-h-[500px]">
                {rows.map(r => (
                  <div key={r.id} className={`px-3 py-2 flex items-center gap-2 border-b border-white/10 group ${col.sel===r.id ? "bg-white/5" : "hover:bg-white/[0.02]"}`}>
                    <button onClick={()=>col.setSel(r.id)} className="flex-1 text-left flex items-center gap-2" data-testid={`${lv.key}-${r.id}`}>
                      <span className="text-sm">{r.name}</span>
                      {r.code && <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{r.code}</span>}
                      {lv.key !== "polling_unit" && <ChevronRight size={12} className="ml-auto text-zinc-600"/>}
                    </button>
                    <button onClick={()=>remove(r.id, lv.key)} className="p-1 text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 size={12}/></button>
                  </div>
                ))}
                {!rows.length && col.active() && <div className="p-4 text-xs text-zinc-500">Empty. Add one above.</div>}
                {!col.active() && <div className="p-4 text-xs text-zinc-600">Select parent →</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
